import { useRef, useState, useMemo } from "react";
import { msg, plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { CloudUpload, X, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppDialog } from "@/components/common/AppDialog";
import { useAuth } from "@/hooks/useAuth";
import { organizationApi } from "@/lib/api/services/organization.api";
import {
  parseImportFile,
  createParsedRecipes,
  createParsedIngredients,
  type DraftRecipeItem,
  type DraftIngredientItem,
} from "@/lib/import/recipes-excel";
import { getMenuItemExportColumns } from "@/components/recipes/tables/menu-items/export-columns";
import { getPrepRecipeExportColumns } from "@/components/recipes/tables/prep-recipes/export-columns";
import { getIngredientExportColumns } from "@/components/recipes/tables/ingredients/export-columns";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const KITCHEN_STATIONS = [
  { value: "Hot", label: msg`Hot` },
  { value: "Cold", label: msg`Cold` },
  { value: "Grill", label: msg`Grill` },
  { value: "Fry", label: msg`Fry` },
  { value: "Sauté", label: msg`Sauté` },
  { value: "Prep", label: msg`Prep` },
  { value: "Pastry", label: msg`Pastry` },
  { value: "Salad", label: msg`Salad` },
  { value: "Dessert", label: msg`Dessert` },
  { value: "Beverage", label: msg`Beverage` },
];

const PREP_TIME_OPTIONS = [
  { label: msg`5 min`, value: 5 },
  { label: msg`10 min`, value: 10 },
  { label: msg`15 min`, value: 15 },
  { label: msg`20 min`, value: 20 },
  { label: msg`30 min`, value: 30 },
  { label: msg`45 min`, value: 45 },
  { label: msg`1 hour`, value: 60 },
  { label: msg`1.5 hours`, value: 90 },
  { label: msg`2 hours`, value: 120 },
  { label: msg`3 hours`, value: 180 },
  { label: msg`4 hours`, value: 240 },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ImportStep = "upload" | "processing" | "review" | "saving";
type ReviewTab = "menuItems" | "prepRecipes" | "ingredients";

interface ImportRecipesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete?: () => void;
}

// ---------------------------------------------------------------------------
// Recipe row sub-component (review step)
// ---------------------------------------------------------------------------

interface RecipeRowProps {
  item: DraftRecipeItem;
  onUpdate: (
    localId: string,
    field: "subcategory" | "prepTimeMinutes",
    value: string | number | undefined,
  ) => void;
  onRemove: (localId: string) => void;
  disabled?: boolean;
}

function RecipeRow({ item, onUpdate, onRemove, disabled }: RecipeRowProps) {
  const { t } = useLingui();
  return (
    <div className="flex items-center gap-3 rounded-[0.625rem] border border-(--color-border) bg-(--color-surface-muted)/50 p-3">
      {/* Thumbnail placeholder */}
      <div className="size-17 shrink-0 rounded-[0.625rem] border border-(--color-border) bg-(--color-surface-muted)" />

      {/* Name + code */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="truncate text-base font-medium">{item.name}</p>
          {item.slug && (
            <p className="truncate text-sm text-(--color-text-secondary)">{item.slug}</p>
          )}
        </div>

        {/* Dropdowns */}
        <div className="flex shrink-0 items-start gap-2">
          {/* Allergens — display only */}
          <div className="flex w-40 flex-col gap-2">
            <p className="text-sm text-(--color-text-secondary)">{t`Allergens:`}</p>
            <div className="flex h-10 w-full items-center justify-between rounded-lg border border-(--color-border) bg-(--color-surface-page) px-3 shadow-sm">
              <span
                className={
                  item.allergenText
                    ? "text-sm text-(--color-text-primary)"
                    : "text-sm text-(--color-text-secondary)"
                }
              >
                {item.allergenText ?? "—"}
              </span>
            </div>
          </div>

          {/* Station → subcategory */}
          <div className="flex w-40 flex-col gap-2">
            <p className="text-sm text-(--color-text-secondary)">{t`Station:`}</p>
            <Select
              value={item.subcategory ?? ""}
              onValueChange={(v) => onUpdate(item.localId, "subcategory", v || undefined)}
              disabled={disabled}
            >
              <SelectTrigger className="h-10 w-full">
                <SelectValue placeholder={t`Select`} />
              </SelectTrigger>
              <SelectContent>
                {KITCHEN_STATIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {t(s.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Prep time → prepTimeMinutes */}
          <div className="flex w-40 flex-col gap-2">
            <p className="text-sm text-(--color-text-secondary)">{t`Prep time:`}</p>
            <Select
              value={item.prepTimeMinutes != null ? String(item.prepTimeMinutes) : ""}
              onValueChange={(v) =>
                onUpdate(item.localId, "prepTimeMinutes", v ? Number(v) : undefined)
              }
              disabled={disabled}
            >
              <SelectTrigger className="h-10 w-full">
                <SelectValue placeholder={t`Select`} />
              </SelectTrigger>
              <SelectContent>
                {PREP_TIME_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {t(opt.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Remove button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onRemove(item.localId)}
        disabled={disabled}
        className="size-11 shrink-0 rounded-[0.625rem] bg-(--color-danger) text-(--color-danger-fg) hover:bg-(--color-danger)/90 disabled:opacity-50"
      >
        <X className="size-5" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ingredient row sub-component (review step)
// ---------------------------------------------------------------------------

interface IngredientRowProps {
  item: DraftIngredientItem;
  onUpdate: (localId: string, type: "RAW" | "COMPOUND" | "READY_TO_USE" | undefined) => void;
  onRemove: (localId: string) => void;
  disabled?: boolean;
}

function IngredientRow({ item, onUpdate, onRemove, disabled }: IngredientRowProps) {
  const { t } = useLingui();
  return (
    <div className="flex items-center gap-3 rounded-[0.625rem] border border-(--color-border) bg-(--color-surface-muted)/50 p-3">
      {/* Thumbnail placeholder */}
      <div className="size-17 shrink-0 rounded-[0.625rem] border border-(--color-border) bg-(--color-surface-muted)" />

      {/* Name + SKU */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="truncate text-base font-medium">{item.name}</p>
          {item.sku && (
            <p className="truncate text-sm text-(--color-text-secondary)">{item.sku}</p>
          )}
        </div>

        {/* Dropdowns */}
        <div className="flex shrink-0 items-start gap-2">
          {/* Allergens — display only */}
          <div className="flex w-40 flex-col gap-2">
            <p className="text-sm text-(--color-text-secondary)">{t`Allergens:`}</p>
            <div className="flex h-10 w-full items-center justify-between rounded-lg border border-(--color-border) bg-(--color-surface-page) px-3 shadow-sm">
              <span
                className={
                  item.allergenText
                    ? "text-sm text-(--color-text-primary)"
                    : "text-sm text-(--color-text-secondary)"
                }
              >
                {item.allergenText ?? "—"}
              </span>
            </div>
          </div>

          {/* Type → RAW / COMPOUND / READY_TO_USE */}
          <div className="flex w-40 flex-col gap-2">
            <p className="text-sm text-(--color-text-secondary)">{t`Type:`}</p>
            <Select
              value={item.type ?? ""}
              onValueChange={(v) =>
                onUpdate(
                  item.localId,
                  v ? (v as "RAW" | "COMPOUND" | "READY_TO_USE") : undefined,
                )
              }
              disabled={disabled}
            >
              <SelectTrigger className="h-10 w-full">
                <SelectValue placeholder={t`Select`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="RAW">{t`Raw`}</SelectItem>
                <SelectItem value="COMPOUND">{t`Compound`}</SelectItem>
                <SelectItem value="READY_TO_USE">{t`Ready to Use`}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Unit of Measure — display only */}
          <div className="flex w-40 flex-col gap-2">
            <p className="text-sm text-(--color-text-secondary)">{t`Unit:`}</p>
            <div className="flex h-10 w-full items-center rounded-lg border border-(--color-border) bg-(--color-surface-page) px-3 shadow-sm">
              <span
                className={
                  item.unitOfMeasure
                    ? "text-sm text-(--color-text-primary)"
                    : "text-sm text-(--color-text-secondary)"
                }
              >
                {item.unitOfMeasure || "—"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Remove button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onRemove(item.localId)}
        disabled={disabled}
        className="size-11 shrink-0 rounded-[0.625rem] bg-(--color-danger) text-(--color-danger-fg) hover:bg-(--color-danger)/90 disabled:opacity-50"
      >
        <X className="size-5" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

export function ImportRecipesDialog({
  open,
  onOpenChange,
  onImportComplete,
}: ImportRecipesDialogProps) {
  const { i18n, t } = useLingui();
  const { user, hasRole, hasPermission } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);

  // Org picker is only for super admins — regular users never see it and
  // organizationId is never sent on their behalf.
  const isSuperAdmin = hasPermission('*:*') || hasRole("SUPER_ADMIN");

  const [step, setStep] = useState<ImportStep>("upload");
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [reviewTab, setReviewTab] = useState<ReviewTab>("menuItems");
  const [menuItemDrafts, setMenuItemDrafts] = useState<DraftRecipeItem[]>([]);
  const [prepRecipeDrafts, setPrepRecipeDrafts] = useState<DraftRecipeItem[]>([]);
  const [ingredientDrafts, setIngredientDrafts] = useState<DraftIngredientItem[]>([]);
  // Explicit pick from the selector. Starts undefined so the useState initializer
  // is not affected by the auth loading race. We derive the effective value below.
  const [selectedOrgId, setSelectedOrgId] = useState<number | undefined>();

  // The org the import will target:
  //   - super admin → their explicit pick, falling back to their own org if set
  //   - everyone else → undefined (API uses the caller's org from the token)
  const effectiveOrgId = isSuperAdmin
    ? (selectedOrgId ?? user?.organization?.id)
    : undefined;

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => organizationApi.getAll(),
    enabled: isSuperAdmin,
    staleTime: 1000 * 60 * 5,
  });

  const menuItemColumns = useMemo(() => getMenuItemExportColumns(i18n), [i18n]);
  const prepRecipeColumns = useMemo(() => getPrepRecipeExportColumns(i18n), [i18n]);
  const ingredientColumns = useMemo(() => getIngredientExportColumns(i18n), [i18n]);

  // Reset all state when modal closes
  const handleOpenChange = (val: boolean) => {
    if (!val) {
      setStep("upload");
      setProgress(0);
      setTotal(0);
      setReviewTab("menuItems");
      setMenuItemDrafts([]);
      setPrepRecipeDrafts([]);
      setIngredientDrafts([]);
      setSelectedOrgId(undefined);
    }
    onOpenChange(val);
  };

  // Parse file → show review step
  const handleFile = async (file: File) => {
    if (!file) return;
    setStep("processing");
    setProgress(0);
    setTotal(0);

    try {
      const parsed = await parseImportFile(file, menuItemColumns, prepRecipeColumns, ingredientColumns);
      setMenuItemDrafts(parsed.menuItems);
      setPrepRecipeDrafts(parsed.prepRecipes);
      setIngredientDrafts(parsed.ingredients);
      setStep("review");
      setReviewTab("menuItems");
    } catch {
      setStep("upload");
      toast.error(i18n._("Failed to parse the file. Please check the format and try again."));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset input so the same file can be re-selected
    e.target.value = "";
  };

  // Update a single field on a draft item
  const updateDraft = (
    setter: React.Dispatch<React.SetStateAction<DraftRecipeItem[]>>,
    localId: string,
    field: "subcategory" | "prepTimeMinutes",
    value: string | number | undefined,
  ) => {
    setter((prev) =>
      prev.map((item) => (item.localId === localId ? { ...item, [field]: value } : item)),
    );
  };

  const removeDraft = (
    setter: React.Dispatch<React.SetStateAction<DraftRecipeItem[]>>,
    localId: string,
  ) => {
    setter((prev) => prev.filter((item) => item.localId !== localId));
  };

  // Save all draft items to the API
  const handleSave = async () => {
    const recipeItems = [...menuItemDrafts, ...prepRecipeDrafts];
    if (recipeItems.length === 0 && ingredientDrafts.length === 0) {
      handleOpenChange(false);
      return;
    }

    setStep("saving");
    setProgress(0);
    setTotal(recipeItems.length + ingredientDrafts.length);

    let totalCreated = 0;
    let totalFailed = 0;
    let globalProgress = 0;

    try {
      if (recipeItems.length > 0) {
        const recipeResult = await createParsedRecipes(
          recipeItems,
          (current) => {
            setProgress(globalProgress + current);
          },
          effectiveOrgId,
        );
        totalCreated += recipeResult.created;
        totalFailed += recipeResult.failed.length;
        globalProgress += recipeItems.length;
      }

      if (ingredientDrafts.length > 0) {
        const ingredientResult = await createParsedIngredients(
          ingredientDrafts,
          (current) => {
            setProgress(globalProgress + current);
          },
          effectiveOrgId,
        );
        totalCreated += ingredientResult.created;
        totalFailed += ingredientResult.failed.length;
      }

      if (totalCreated > 0) {
        toast.success(
          i18n._(
            t`${totalCreated} ${plural(totalCreated, {
              one: "item",
              other: "items",
            })} imported successfully`,
          ),
        );
        onImportComplete?.();
      }

      if (totalFailed > 0) {
        toast.warning(
          i18n._(
            t`${totalFailed} ${plural(totalFailed, {
              one: "item",
              other: "items",
            })} failed to import`,
          ),
        );
      }
    } catch {
      toast.error(i18n._("Import failed. Please try again."));
    }

    handleOpenChange(false);
  };

  const pct = total > 0 ? Math.round((progress / total) * 100) : 0;
  const allDraftsCount = menuItemDrafts.length + prepRecipeDrafts.length + ingredientDrafts.length;
  const isSaving = step === "saving";
  const isReview = step === "review" || isSaving;

  // Compute modal props for each step
  const modalTitle =
    step === "upload" ? i18n._("Import recipes") : i18n._("Imported recipes");

  const modalDescription =
    step === "upload"
      ? i18n._("Upload an Excel or CSV file exported from this page to bulk-create recipes.")
      : undefined;

  const modalClassName = isReview ? "max-w-250" : undefined;
  const modalHideClose = step === "processing" || isSaving;

  // ---------------------------------------------------------------------------
  // Review tab items
  // ---------------------------------------------------------------------------

  const activeTabItems = reviewTab === "menuItems" ? menuItemDrafts : prepRecipeDrafts;
  const activeSetDrafts =
    reviewTab === "menuItems" ? setMenuItemDrafts : setPrepRecipeDrafts;

  const tabs: Array<{ id: ReviewTab; label: string; count: number }> = [
    { id: "menuItems", label: i18n._("Menu Items"), count: menuItemDrafts.length },
    { id: "prepRecipes", label: i18n._("Prep Recipes"), count: prepRecipeDrafts.length },
    { id: "ingredients", label: i18n._("Ingredients"), count: ingredientDrafts.length },
  ];

  // ---------------------------------------------------------------------------
  // Review step footer
  // ---------------------------------------------------------------------------

  const reviewFooter = (
    <div className="flex w-full items-center justify-end gap-3">
      <Button
        variant="outline"
        onClick={() => handleOpenChange(false)}
        disabled={isSaving}
        className="h-13 min-w-55 rounded-[0.625rem]"
      >
        {i18n._("Cancel")}
      </Button>
      <Button
        onClick={handleSave}
        disabled={isSaving || allDraftsCount === 0}
        className="h-13 min-w-55 gap-2 rounded-[0.625rem] bg-(--color-text-primary) text-(--color-surface-page) hover:bg-(--color-text-primary)/90"
      >
        {isSaving ? (
          <Loader2 className="size-5 animate-spin" />
        ) : (
          <CloudUpload className="size-5" />
        )}
        {isSaving ? i18n._("Saving…") : i18n._("Save to the Catalog")}
      </Button>
    </div>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={modalTitle}
      description={modalDescription}
      className={modalClassName}
      hideClose={modalHideClose}
      footer={isReview ? reviewFooter : undefined}
    >
      {/* ------------------------------------------------------------------ */}
      {/* UPLOAD STEP                                                         */}
      {/* ------------------------------------------------------------------ */}
      {step === "upload" && (
        <div className="flex flex-col gap-4">
          {/* Org selector — visible only for super admins without a default org */}
          {isSuperAdmin && (
            <div className="flex flex-col gap-2 rounded-[0.625rem] border border-(--color-border) bg-(--color-surface-muted)/50 p-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Building2 className="size-4 text-(--color-text-secondary)" />
                {i18n._("Target organization")}
              </div>
              <Select
                value={effectiveOrgId != null ? String(effectiveOrgId) : ""}
                onValueChange={(v) => setSelectedOrgId(v ? Number(v) : undefined)}
              >
                <SelectTrigger className="h-10 w-full">
                  <SelectValue placeholder={i18n._("Select an organization…")} />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={String(org.id)}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-(--color-text-secondary)">
                {i18n._("All imported recipes and ingredients will be assigned to this organization.")}
              </p>
            </div>
          )}

          <Button
            variant="ghost"
            className="flex h-auto w-full flex-col items-center gap-4 rounded-xl border-2 border-dashed border-(--color-border) bg-(--color-surface-muted)/40 px-6 py-14 text-center hover:bg-(--color-surface-muted)/60 disabled:pointer-events-none disabled:opacity-50"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            disabled={isSuperAdmin && !effectiveOrgId}
          >
            <div className="flex size-18 items-center justify-center rounded-full bg-(--color-surface-page) shadow-sm border border-(--color-border)">
              <CloudUpload className="size-8 text-(--color-text-secondary)" />
            </div>
            <div>
              <p className="text-base font-medium">{i18n._("Upload media")}</p>
              <p className="mt-1 text-sm text-(--color-text-secondary)">
                {isSuperAdmin && !effectiveOrgId
                  ? i18n._("Select an organization above before uploading.")
                  : i18n._("File should not exceed 200mb or 2 min for video.")}
              </p>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.csv"
              className="hidden"
              onChange={handleInputChange}
            />
          </Button>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* PROCESSING STEP                                                     */}
      {/* ------------------------------------------------------------------ */}
      {step === "processing" && (
        <div className="flex flex-col items-center gap-6 py-6">
          {/* Circular progress */}
          <div className="relative flex size-24 items-center justify-center">
            <svg className="absolute inset-0 size-full -rotate-90" viewBox="0 0 96 96">
              <circle
                cx="48"
                cy="48"
                r="40"
                fill="none"
                stroke="currentColor"
                strokeWidth="6"
                className="text-border"
              />
              <circle
                cx="48"
                cy="48"
                r="40"
                fill="none"
                stroke="currentColor"
                strokeWidth="6"
                strokeDasharray={`${2 * Math.PI * 40}`}
                strokeDashoffset={`${2 * Math.PI * 40 * (1 - pct / 100)}`}
                className="text-(--color-text-primary) transition-all duration-300"
                strokeLinecap="round"
              />
            </svg>
            <span className="text-sm font-medium">{pct}%</span>
          </div>

          <div className="text-center">
            <p className="text-base font-medium">{i18n._("Loading…")}</p>
            <p className="mt-1 text-sm text-(--color-text-secondary)">
              {i18n._("We're currently working on your file. Please allow some time for processing.")}
            </p>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* REVIEW STEP (includes saving state)                                */}
      {/* ------------------------------------------------------------------ */}
      {isReview && (
        <div className="space-y-4">
          {/* Selected org indicator — super admins only */}
          {isSuperAdmin && effectiveOrgId != null && (
            <div className="flex items-center gap-2 rounded-[0.625rem] border border-(--color-border) bg-(--color-surface-muted)/50 px-4 py-2.5">
              <Building2 className="size-4 shrink-0 text-(--color-text-secondary)" />
              <span className="text-sm text-(--color-text-secondary)">{i18n._("Organization:")}</span>
              <span className="text-sm font-medium">
                {organizations.find((o) => o.id === effectiveOrgId)?.name ?? String(effectiveOrgId)}
              </span>
            </div>
          )}

          {/* Tab bar */}
          <div className="flex gap-1 rounded-[0.375rem] bg-(--color-surface-muted) p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => !isSaving && setReviewTab(tab.id)}
                className={[
                  "flex items-center gap-1.5 rounded-[0.25rem] px-4 py-2 text-sm transition-colors",
                  tab.id === reviewTab
                    ? "bg-(--color-surface-page) font-medium text-(--color-text-primary) shadow-sm"
                    : "text-(--color-text-secondary) hover:text-(--color-text-primary)",
                  isSaving ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                ].join(" ")}
              >
                {tab.label}
                {tab.count > 0 && (
                  <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs">
                    {tab.count}
                  </Badge>
                )}
              </button>
            ))}
          </div>

          {/* Item list */}
          {reviewTab === "ingredients" ? (
            ingredientDrafts.length === 0 ? (
              <div className="flex h-32 items-center justify-center rounded-xl border border-dashed border-(--color-border)">
                <p className="text-sm text-(--color-text-secondary)">
                  {i18n._("No items found in this category.")}
                </p>
              </div>
            ) : (
              <div className="max-h-104 space-y-3 overflow-y-auto pr-1">
                {ingredientDrafts.map((item) => (
                  <IngredientRow
                    key={item.localId}
                    item={item}
                    disabled={isSaving}
                    onUpdate={(localId, type) =>
                      setIngredientDrafts((prev) =>
                        prev.map((i) => (i.localId === localId ? { ...i, type } : i)),
                      )
                    }
                    onRemove={(localId) =>
                      setIngredientDrafts((prev) =>
                        prev.filter((i) => i.localId !== localId),
                      )
                    }
                  />
                ))}
              </div>
            )
          ) : activeTabItems.length === 0 ? (
            <div className="flex h-32 items-center justify-center rounded-xl border border-dashed border-(--color-border)">
              <p className="text-sm text-(--color-text-secondary)">
                {i18n._("No items found in this category.")}
              </p>
            </div>
          ) : (
            <div className="max-h-104 space-y-3 overflow-y-auto pr-1">
              {activeTabItems.map((item) => (
                <RecipeRow
                  key={item.localId}
                  item={item}
                  disabled={isSaving}
                  onUpdate={(localId, field, value) =>
                    updateDraft(activeSetDrafts, localId, field, value)
                  }
                  onRemove={(localId) => removeDraft(activeSetDrafts, localId)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </AppDialog>
  );
}
